import { useContext, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from "@mui/material";
import { SnackbarContext } from "../../../contexts/Snackbar";
import {
  AttachFile,
  CloudUpload,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { StepIcon } from "../../../features/appointments/singleView/components/utils/FinishMeetingUtils";
import AppendFilesStep from "./steps/AppendFilesStep";
import EditFilesStep from "./steps/EditFilesStep";
import UploadFilesStep from "./steps/UploadFilesStep";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { fileToBase64 } from "./FileUploadEditingForm";
import usePutAppointmentFiles from "./hooks/usePutAppointmentFiles";
import { useParams } from "react-router-dom";
import { APPOINTMENT_FILES } from "../../../enums/QueryKeys";
import {
  getTitleWithoutExtension,
  getFileExtension,
  getFileSignature,
} from "./utils/FileUploadFunctions";
import { snackbarSeverity } from "../../../hooks/useSnackbar";
import usePrimaryColor from "../../../hooks/usePrimaryColor";
import { useIsMobile } from "../../../utils/Functions";
import CloseIcon from "@mui/icons-material/Close";

// asButton renders a labelled button instead of the attach icon button
export default function FileUploadStepper({ endpoint = null, asButton = false }) {
  const { t } = useTranslation();
  const { id: appointmentId } = useParams();
  const { handleSnackbar } = useContext(SnackbarContext);
  const title = "UploadFile";
  const attachFileIconColor = usePrimaryColor();
  const isMobile = useIsMobile();

  //navigation states
  const [activeStep, setActiveStep] = useState(0);
  const [open, setOpen] = useState(false);

  // Form setup
  const methods = useForm({ defaultValues: { files: [] } });
  const { control, reset, handleSubmit } = methods;
  const { append, remove } = useFieldArray({ name: "files", control });

  // File states
  const [selectedFiles, setSelectedFiles] = useState([]);
  const { submitCallback } = usePutAppointmentFiles(
    endpoint,
    APPOINTMENT_FILES + appointmentId
  );

  const handleClickOpen = () => {
    setActiveStep(0);
    setOpen(true);
  };

  const handleClickClose = () => {
    setOpen(false);
    setSelectedFiles([]);
    reset({ files: [] });
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  function handleFilesSelected(newFiles) {
    if (!newFiles?.length) return;

    setSelectedFiles((prev) => {
      const existingSignatures = new Set(prev.map(getFileSignature));
      const filteredNewFiles = newFiles.filter(
        (f) => !existingSignatures.has(getFileSignature(f))
      );
      if (!filteredNewFiles.length || filteredNewFiles.length < 1) return prev;

      const metadataPlaceholders = filteredNewFiles.map((f) => ({
        title: getTitleWithoutExtension(f.name),
        category: "",
      }));
      append(metadataPlaceholders);
      return [...prev, ...filteredNewFiles];
    });
  }

  const handleFileDelete = (fileToDelete) => {
    setSelectedFiles((prevVal) => {
      const indexToDelete = prevVal.findIndex(
        (file) => getFileSignature(file) === getFileSignature(fileToDelete)
      );
      if (indexToDelete > -1) {
        // Remove from  React Hook Form's field array by index
        remove(indexToDelete);
        // Remove from selectedFiles state
        return prevVal.filter((_, index) => index !== indexToDelete);
      }
      return prevVal;
    });
  };

  const handleUploadAll = async (data) => {
    const submitData = await Promise.all(
      data?.files?.map(async (fileData, index) => {
        const file = selectedFiles[index];
        if (file) {
          var base64File = await fileToBase64(file);
          const fileExtension = getFileExtension(file.name);
          const inputedTitle = getTitleWithoutExtension(fileData?.title);
          const fileTitleWithExtension = fileData?.title
            ? `${inputedTitle}.${fileExtension}`
            : file.name;
          return {
            file: base64File,
            fileTitle: fileTitleWithExtension,
            documentCategory: fileData?.category,
          };
        }
      })
    );
    //For some reason getting null or undefined data in array so removing that
    const filteredData = submitData.filter(
      (item) => item !== undefined && item !== null
    );
    handleSnackbar(
      "UploadingYourFilesThisMightTakeAWhile",
      snackbarSeverity.INFO,
      true,
      true
    );
    handleClickClose();
    await submitCallback(filteredData);
  };

  const steps = [
    {
      title: "AddFiles",
      body: (
        <AppendFilesStep
          key={selectedFiles.length} //DropzoneArea holds file references internally; key clears them on count change.
          selectedFiles={selectedFiles}
          onFilesSelected={handleFilesSelected}
          onFileDelete={handleFileDelete}
        />
      ),
    },
    {
      title: "EditFileList",
      body: (
        <EditFilesStep
          selectedFiles={selectedFiles}
          onFileDelete={handleFileDelete}
        />
      ),
    },
    {
      title: "FileUploadAndProgress",
      body: <UploadFilesStep selectedFiles={selectedFiles} />,
    },
  ];

  const isLastStep = activeStep === steps.length - 1;

  return (
    <div>
      {asButton ? (
        <Button
          variant="contained"
          size="small"
          startIcon={<AttachFile />}
          onClick={() => handleClickOpen()}
        >
          {t(title)}
        </Button>
      ) : (
        // Outlined to match the grid/table ToggleButtonGroup beside it
        <Tooltip title={t(title)}>
          <IconButton
            sx={{
              p: "7px",
              borderRadius: 1,
              border: 1,
              borderColor: "divider",
              color: attachFileIconColor,
            }}
            onClick={() => handleClickOpen()}
          >
            <AttachFile fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <FormProvider {...methods}>
        <Dialog
          maxWidth="md"
          fullWidth
          open={open}
          fullScreen={isMobile}
          PaperProps={{ sx: { borderRadius: isMobile ? 0 : 4 } }}
        >
          <FileUploadDialogTitle
            title={t(title)}
            iconColor={attachFileIconColor}
            stepCaption={`${t("StepOf", {
              current: activeStep + 1,
              total: steps.length,
            })} · ${t(steps[activeStep].title)}`}
            onClose={handleClickClose}
          />

          <Divider />

          <DialogContent sx={{ px: { xs: 2, sm: 4 }, py: 3, minHeight: 100 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label, index) => (
                <Step key={index}>
                  <StepLabel StepIconComponent={StepIcon}>
                    {t(label.title)}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            <Box mt={4}>{steps[activeStep].body}</Box>
          </DialogContent>

          <Divider />

          <FileUploadDialogActions
            isMobile={isMobile}
            isLastStep={isLastStep}
            canGoBack={activeStep !== 0}
            hasFiles={selectedFiles.length > 0}
            onBack={handleBack}
            onNext={handleNext}
            onUpload={handleSubmit(handleUploadAll)}
          />
        </Dialog>
      </FormProvider>
    </div>
  );
}

function FileUploadDialogTitle({ title, iconColor, stepCaption, onClose }) {
  const { t } = useTranslation();
  return (
    <DialogTitle
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        px: { xs: 2, sm: 3 },
        py: 2,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
        <Avatar
          variant="rounded"
          sx={{
            bgcolor: iconColor,
            width: 40,
            height: 40,
            borderRadius: 2,
          }}
        >
          <AttachFile fontSize="small" />
        </Avatar>
        <Box minWidth={0}>
          <Typography variant="h6" fontWeight={600} lineHeight={1.25}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="p">
            {stepCaption}
          </Typography>
        </Box>
      </Stack>
      <IconButton onClick={onClose} title={t("Close")}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
  );
}

function FileUploadDialogActions({
  isMobile,
  isLastStep,
  canGoBack,
  hasFiles,
  onBack,
  onNext,
  onUpload,
}) {
  const { t } = useTranslation();
  const size = isMobile ? "small" : "medium";
  return (
    <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, gap: 1 }}>
      <Button
        onClick={onBack}
        disabled={!canGoBack}
        startIcon={<NavigateBefore />}
        size={size}
      >
        {t("Back")}
      </Button>
      <Box flex={1} />
      {isLastStep ? (
        <Button
          variant="contained"
          onClick={onUpload}
          disabled={!hasFiles}
          startIcon={<CloudUpload />}
          size={size}
        >
          {t("AddFiles")}
        </Button>
      ) : (
        <Button
          variant="contained"
          onClick={onNext}
          disabled={!hasFiles}
          endIcon={<NavigateNext />}
          size={size}
        >
          {t("Next")}
        </Button>
      )}
    </DialogActions>
  );
}
